import {
  MINDMAP_CHIP_ALLOWANCE,
  MINDMAP_PILL_BORDER_CHROME,
  MINDMAP_PILL_GLYPH_ALLOWANCE,
  MINDMAP_PILL_MAX_WIDTH,
  mindmapLabelBoxWidth,
  mindmapPillHeight,
  mindmapPillLineCount,
  mindmapTextWidth,
} from "../../excalidraw/element/mindmapPillStyle";

import type { MindmapTree } from "./mindmapTree";

/**
 * Pure right-growing tidy-tree layout for the auto-mindmap.
 *
 * The root sits at the origin and EVERY subtree fans rightward from it, the
 * whole fan vertically centered on the root — the XMind/NotebookLM shape the
 * owner approved. (V2 balanced subtrees left and right; a two-sided map read as
 * two competing documents and made the root's own label the visual centre of
 * nothing.) Depth maps to horizontal distance from the root, siblings stack
 * vertically in disjoint bands so no two boxes overlap, and output is
 * deterministic — no randomness, input order preserved.
 */

/** Horizontal gap between a node box and its children boxes. */
export const H_GAP = 80;
/** Vertical gap between stacked sibling boxes. */
export const V_GAP = 24;

/**
 * Height of a one-line pill. Kept as the default box height for the paths that
 * size a pill without a tree node in hand (the generation placeholder, the
 * standalone card); a real node measures its own label via `mindmapNodeHeight`.
 */
export const PILL_HEIGHT = mindmapPillHeight(1);

/**
 * Extra width a page pill reserves for the Notion glyph the renderer draws: the
 * 18px glyph box (RenderMindmapNode's `.mindmap-node-glyph`) plus the flex `gap`
 * (8px) between it and the label, so a page node's label isn't crowded.
 */
export const PAGE_GLYPH_ALLOWANCE = MINDMAP_PILL_GLYPH_ALLOWANCE;

/**
 * Extra width an EXPANDABLE node reserves so its round expand/collapse chip
 * renders fully INSIDE the element's box. The chip previously overhung
 * the pill's trailing edge and was clipped by the integration container's
 * `overflow: hidden`; folding its footprint into the box width fixes that
 * structurally. RenderMindmapNode draws the pill at `width - CHIP_ALLOWANCE` and
 * seats the chip in the reserved gap. Re-exported from the shared pill style so
 * the layout and the PNG/SVG exports can never disagree about it.
 */
export const CHIP_ALLOWANCE = MINDMAP_CHIP_ALLOWANCE;

/** Whether a tree node renders with an expand/collapse chip (matches the
 * emitter's `expandable`): it has children and is not a truncation stub. */
export const isExpandableTree = (tree: MindmapTree): boolean =>
  tree.hasChildren === true && tree.truncated !== true;

/**
 * Horizontal chrome around the label, matching RenderMindmapNode's body padding
 * of `0 16px` (16px each side = 32px). Under-counting this truncated short pills
 * (a "Duplicate" page pill rendered as "Du…"): the estimate has to reserve the
 * SAME padding the DOM pill applies, or the label ellipsizes with room to spare.
 */
const NODE_PADDING = 32;
const MIN_WIDTH = 60;

/**
 * Slack the box reserves beyond the measured label. `mindmapTextWidth` buckets
 * glyphs into three widths, so a label can come out a few pixels short of what
 * the font really draws — and a box short by one pixel breaks its last word.
 * Reserved on the BOX only, never on the label box the renderer wraps in, so the
 * two still agree about where the text is allowed to go.
 */
const LABEL_SLACK = 4;

/**
 * Non-label width a node's box carries: padding, the Notion glyph, the node's
 * own icon and the chip allowance. Icons were missing here while the DOM pill
 * laid one out, so an icon'd node's label wrapped where the estimator said it
 * would not — the sizes come from `mindmapLabelBoxWidth`'s constants so
 * the two can no longer drift.
 */
const chromeWidth = (tree: MindmapTree): number =>
  NODE_PADDING +
  MINDMAP_PILL_BORDER_CHROME +
  LABEL_SLACK +
  (tree.kind === "page" ? PAGE_GLYPH_ALLOWANCE : 0) +
  (tree.icon ? PAGE_GLYPH_ALLOWANCE : 0) +
  (isExpandableTree(tree) ? CHIP_ALLOWANCE : 0);

/**
 * The on-canvas pill width for a node, sized to RenderMindmapNode's intrinsic
 * box so short/medium labels never ellipsize: the label estimate plus the node's
 * chrome, clamped to the shared max width. Beyond that the label wraps rather
 * than widening further. Exported so the refresh/expand incremental-add paths
 * size new pills exactly as generation does.
 */
export const mindmapNodeWidth = (tree: MindmapTree): number =>
  Math.max(
    MIN_WIDTH,
    Math.min(
      MINDMAP_PILL_MAX_WIDTH,
      mindmapTextWidth(tree.text) + chromeWidth(tree),
    ),
  );

/**
 * The on-canvas pill height for a node: one line, or two once the label no
 * longer fits the max-width box (V3 — labels wrap to a second line before they
 * ellipsize). Exported alongside the width so every path that places a pill —
 * generation, expand, refresh — reserves the same box the renderer draws.
 */
export const mindmapNodeHeight = (tree: MindmapTree): number =>
  mindmapPillHeight(
    // The label box the DOM pill will actually wrap in — same function the
    // renderer and the exports use, so the reserved height matches the wrap.
    mindmapPillLineCount(tree.text, mindmapTreeLabelBoxWidth(tree)),
  );

/** The label box `mindmapNodeWidth` leaves this tree node, via the shared rule. */
export const mindmapTreeLabelBoxWidth = (tree: MindmapTree): number =>
  mindmapLabelBoxWidth({
    elementWidth: mindmapNodeWidth(tree),
    expandable: isExpandableTree(tree),
    isPage: tree.kind === "page",
    hasIcon: !!tree.icon,
  });

export interface MindmapLayoutNode {
  blockId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MindmapLayoutEdge {
  fromBlockId: string;
  toBlockId: string;
}

export interface MindmapLayout {
  nodes: MindmapLayoutNode[];
  edges: MindmapLayoutEdge[];
}

/** Estimated label-box width for `text` — a coarse char-width heuristic. */
export const estimateWidth = (text: string): number =>
  Math.max(
    MIN_WIDTH,
    Math.min(MINDMAP_PILL_MAX_WIDTH, mindmapTextWidth(text) + NODE_PADDING),
  );

const countLeaves = (tree: MindmapTree): number =>
  tree.children.length === 0
    ? 1
    : tree.children.reduce((sum, child) => sum + countLeaves(child), 0);

/**
 * Assign vertical positions to a subtree using a monotonic cursor. Leaves take
 * the next free band; a parent centers on its children. The cursor is left one
 * V_GAP past the subtree's last leaf, so consecutive calls stack subtrees with
 * exactly one V_GAP between them — the same pitch `subtreeHeight` assumes.
 */
const assignY = (
  tree: MindmapTree,
  cursor: { y: number },
  out: Map<string, number>,
): void => {
  if (tree.children.length === 0) {
    out.set(tree.blockId, cursor.y + mindmapNodeHeight(tree) / 2);
    cursor.y += mindmapNodeHeight(tree) + V_GAP;
    return;
  }
  for (const child of tree.children) {
    assignY(child, cursor, out);
  }
  const first = out.get(tree.children[0].blockId)!;
  const last = out.get(tree.children[tree.children.length - 1].blockId)!;
  out.set(tree.blockId, (first + last) / 2);
};

/** Sum of every leaf's box height in a subtree (leaves stack vertically). */
const sumLeafHeights = (tree: MindmapTree): number =>
  tree.children.length === 0
    ? mindmapNodeHeight(tree)
    : tree.children.reduce((sum, child) => sum + sumLeafHeights(child), 0);

/**
 * Total vertical extent a subtree occupies. Leaves pack consecutively with a
 * V_GAP between each, so the extent is the sum of leaf heights plus the gaps —
 * using each node's real height keeps taller two-line pills from overlapping.
 */
const subtreeHeight = (tree: MindmapTree): number => {
  const leaves = countLeaves(tree);
  return sumLeafHeights(tree) + (leaves - 1) * V_GAP;
};

export const layoutMindmap = (tree: MindmapTree): MindmapLayout => {
  const nodes: MindmapLayoutNode[] = [];
  const edges: MindmapLayoutEdge[] = [];

  const rootWidth = mindmapNodeWidth(tree);
  nodes.push({
    blockId: tree.blockId,
    x: 0,
    y: 0,
    width: rootWidth,
    height: mindmapNodeHeight(tree),
  });

  const subtrees = tree.children;
  if (subtrees.length === 0) {
    return { nodes, edges };
  }

  // One monotonic pass over every top-level subtree. `assignY` already leaves
  // the cursor one V_GAP past the subtree it placed, so the loop adds nothing:
  // adding another gap here gave top-level subtrees a 2·V_GAP pitch while
  // `subtreeHeight` below still assumed V_GAP, skewing the whole fan by
  // (k−1)·V_GAP/2 against the root it is supposed to be centered on.
  const yByBlock = new Map<string, number>();
  const cursor = { y: 0 };
  for (const subtree of subtrees) {
    assignY(subtree, cursor, yByBlock);
  }

  const totalHeight = subtrees.reduce(
    (sum, subtree, i) => sum + subtreeHeight(subtree) + (i > 0 ? V_GAP : 0),
    0,
  );
  const yShift = -totalHeight / 2;

  const place = (node: MindmapTree, parentX: number, parentWidth: number) => {
    const width = mindmapNodeWidth(node);
    const x = parentX + parentWidth / 2 + H_GAP + width / 2;
    const y = yByBlock.get(node.blockId)! + yShift;

    nodes.push({
      blockId: node.blockId,
      x,
      y,
      width,
      height: mindmapNodeHeight(node),
    });

    for (const child of node.children) {
      edges.push({ fromBlockId: node.blockId, toBlockId: child.blockId });
      place(child, x, width);
    }
  };

  for (const subtree of subtrees) {
    edges.push({ fromBlockId: tree.blockId, toBlockId: subtree.blockId });
    place(subtree, 0, rootWidth);
  }

  return { nodes, edges };
};
